// Group Creation API helpers

import { API_CONFIG } from '../../config/api';

export interface GroupCreationRequest {
  groupName: string;
  description: string;
  tags?: string[];
  program?: string;
  course?: string;
  yearLevel?: string;
  maxParticipants?: number;
  consentAgreed: boolean;
  consultantName: string;
  department: string;
}

export interface GroupResponse {
  _id?: string; // MongoDB ID
  id: string;
  groupType?: 'consultant' | 'enterprise';
  groupCategory?: 'student' | 'company' | 'general';
  groupName: string;
  description: string;
  tags: string[];
  program: string;
  course?: string;
  yearLevel: string;
  maxParticipants?: number;
  approvedMembers?: number;
  pendingRequests?: number;
  consultantName?: string; // Optional for enterprise groups
  department?: string; // Optional for enterprise groups
  // Folder and UI fields
  folderId?: string;
  folderAncestors?: string[];
  breadcrumbs?: string[]; // Ordered folder path names for public groups view
  uiGroupType?: 'Student' | 'Course' | 'Faculty' | 'Year' | 'Custom Tags';
  uiVisibility?: 'Public' | 'Invite Only' | 'Code Protected';
  customTagName?: string;
  customTagColor?: string;
  createdBy: {
    id: string;
    firstName?: string;
    lastName?: string;
    preferredName?: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  joinStatus?: 'none' | 'pending' | 'approved' | 'rejected'; // Present in public groups API response
}

export interface PopulatedUser {
  _id: string;
  profile: {
    firstName?: string;
    lastName?: string;
    preferredName?: string;
  };
  email: string;
}

export interface JoinRequest {
  user: PopulatedUser;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  respondedAt?: string;
}

export interface GroupDetailResponse extends Omit<GroupResponse, 'joinStatus'> {
  joinRequests: JoinRequest[];
}

export interface GroupDetailApiResponse {
  success: boolean;
  data: {
    group: GroupDetailResponse;
    counts: {
      pendingRequests: number;
      approvedMembers: number;
    };
  };
}

export interface UpdateJoinRequestResponse {
  success: boolean;
  message: string;
  data: {
    groupId: string;
    userId: string;
    status: 'approved' | 'rejected';
    group: GroupDetailResponse;
    counts: {
      pendingRequests: number;
      approvedMembers: number;
    };
  };
}

export interface UpdateConsultantGroupRequest {
  groupName?: string;
  description?: string;
  tags?: string[];
  program?: string;
  course?: string | null;
  yearLevel?: string;
  maxParticipants?: number | null;
  consultantName?: string;
  department?: string;
}

export interface UpdateConsultantGroupResponse {
  success: boolean;
  message: string;
  data: {
    group: GroupDetailResponse;
    counts: {
      pendingRequests: number;
      approvedMembers: number;
    };
  };
}

export interface GroupCreationResponse {
  success: boolean;
  message: string;
  data: GroupResponse;
}

export interface ApiError {
  success: false;
  message: string;
}

/**
 * Create a new group
 */
export const createGroup = async (
  token: string,
  groupData: GroupCreationRequest
): Promise<GroupCreationResponse> => {
  try {
    const response = await fetch(API_CONFIG.ENDPOINTS.GROUPS, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(groupData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create group');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get groups created by the current consultant
 */
export const getMyGroups = async (
  token: string,
  page = 1,
  limit = 10
): Promise<{
  success: boolean;
  data: {
    groups: GroupResponse[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalGroups: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch groups');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Helper function to get auth token from localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Get public groups that users can request to join
 */
export const getPublicGroups = async (
  token: string,
  page = 1,
  limit = 10
): Promise<{
  success: boolean;
  data: {
    groups: GroupResponse[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalGroups: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/public?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch public groups');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Discover enterprise groups matching user's demographic profile
 */
export const discoverEnterpriseGroups = async (
  token: string,
  page = 1,
  limit = 12
): Promise<{
  success: boolean;
  data: {
    groups: GroupResponse[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalGroups: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/enterprise/discover?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch enterprise groups');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Request to join a group
 */
export const requestJoinGroup = async (
  token: string,
  groupId: string,
  consent?: {
    accepted: boolean;
    textVersion: string;
    text: string;
  }
): Promise<{
  success: boolean;
  message: string;
  data: {
    groupId: string;
    status: string;
  };
}> => {
  try {
    const requestBody = consent ? { consent } : {};

// #region agent log
fetch('http://127.0.0.1:7242/ingest/c66a66ed-19d2-444d-a460-019f834769c6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'groupApi.ts:272',message:'API call to join group',data:{groupId,hasConsent:!!consent,requestBodySize:JSON.stringify(requestBody).length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'D'})}).catch(()=>{});
// #endregion

    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/${groupId}/join-request`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to submit join request');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get detailed information about a specific group (for consultants)
 */
export const getConsultantGroupDetails = async (
  token: string,
  groupId: string
): Promise<GroupDetailApiResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/${groupId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch group details');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Update group metadata (for consultants)
 */
export const updateConsultantGroup = async (
  token: string,
  groupId: string,
  patchData: UpdateConsultantGroupRequest
): Promise<UpdateConsultantGroupResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/${groupId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(patchData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update group');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Update the status of a join request (approve or reject)
 */
export const updateJoinRequestStatus = async (
  token: string,
  groupId: string,
  userId: string,
  status: 'approved' | 'rejected'
): Promise<UpdateJoinRequestResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/${groupId}/join-requests/${userId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update join request status');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Create a new enterprise group
 */
export const createEnterpriseGroup = async (
  token: string,
  groupData: {
    groupName: string;
    description: string;
    tags?: string[];
    consentAgreed: boolean;
  }
): Promise<GroupCreationResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/enterprise`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(groupData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create enterprise group');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get enterprise groups that consultants can request to join
 */
export const getEnterprisePublicGroups = async (
  token: string,
  page = 1,
  limit = 10
): Promise<{
  success: boolean;
  data: {
    groups: GroupResponse[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalGroups: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/enterprise-public?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch enterprise groups');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get enterprise groups created by the current enterprise user
 */
export const getMyEnterpriseGroups = async (
  token: string,
  page = 1,
  limit = 10,
  archived = false  // Explicitly default to non-archived groups
): Promise<{
  success: boolean;
  data: {
    groups: GroupResponse[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalGroups: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      archived: archived.toString()  // Explicitly pass archived status
    });

    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/enterprise?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch enterprise groups');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get all groups under the enterprise hierarchy (including sub-branches/sub-enterprises)
 */
export const getAllEnterpriseGroups = async (
  token: string,
  page = 1,
  limit = 1000
): Promise<{
  success: boolean;
  data: {
    groups: GroupResponse[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalGroups: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/enterprise/all-groups?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();
    console.log('getAllEnterpriseGroups API response:', { status: response.status, data });

    if (!response.ok) {
      const errorMsg = data.message || `Failed to fetch all enterprise groups (Status: ${response.status})`;
      console.error('getAllEnterpriseGroups error:', errorMsg);
      throw new Error(errorMsg);
    }

    return data;
  } catch (error) {
    console.error('getAllEnterpriseGroups fetch error:', error);
    throw error;
  }
};

/**
 * Delete a group
 */
export const deleteGroup = async (
  token: string,
  groupId: string
): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/${groupId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete group');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Restore an archived group
 */
export const restoreGroup = async (
  token: string,
  groupId: string
): Promise<{
  success: boolean;
  message: string;
  data: GroupResponse;
}> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/${groupId}/restore`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to restore group');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Archive a group (soft delete)
 */
export const archiveGroup = async (
  token: string,
  groupId: string
): Promise<{ success: boolean; message: string; data?: { groupId: string; archived: boolean } }> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/${groupId}/archive`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to archive group');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get archived groups created by the current user
 */
export const getArchivedGroups = async (
  token: string,
  page = 1,
  limit = 10
): Promise<{
  success: boolean;
  data: {
    groups: GroupResponse[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalGroups: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      archived: 'true'
    });

    const url = `${API_CONFIG.ENDPOINTS.GROUPS}/enterprise?${params}`;
    console.log('📦 [getArchivedGroups] Fetching from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();
    console.log('📦 [getArchivedGroups] Response:', { status: response.status, ok: response.ok, data });

    if (!response.ok) {
      console.error('❌ [getArchivedGroups] Request failed:', data.message || 'Unknown error');
      throw new Error(data.message || 'Failed to fetch archived groups');
    }

    console.log('✅ [getArchivedGroups] Successfully fetched', data.data?.groups?.length || 0, 'archived groups');
    return data;
  } catch (error) {
    console.error('❌ [getArchivedGroups] Error:', error);
    throw error;
  }
};

/**
 * Manually add a user to a group as an approved member
 */
export const addUserToGroup = async (
  token: string,
  groupId: string,
  email: string
): Promise<{
  success: boolean;
  message: string;
  data: {
    groupId: string;
    userId: string;
    group: GroupDetailResponse;
    counts: {
      pendingRequests: number;
      approvedMembers: number;
    };
  };
}> => {
  try {
    const response = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}/${groupId}/add-member`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: email.trim() })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to add user to group');
    }

    return data;
  } catch (error) {

    throw error;
  }
};

/**
 * Helper function to handle API errors consistently
 */
export const handleApiError = (error: any): ApiError => {
  if (error.message) {
    return {
      success: false,
      message: error.message
    };
  }

  return {
    success: false,
    message: 'An unexpected error occurred'
  };
};
